import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Target,
  CalendarDays,
  Users,
  Settings,
  LogOut,
  ClipboardList,
  FileText,
  BarChart3,
  CheckSquare,
  Building2,
  Shield
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

export const Sidebar = () => {
  const { user, logout, isAdmin, isAdminOrFounder, isSuperAdmin } = useAuth();
  const location = useLocation();

  const superAdminLinks = [
    { to: '/platform', label: 'Companies', icon: Building2 },
  ];

  const adminFounderLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/goals', label: 'Goals', icon: Target },
    { to: '/daily-plan', label: 'Daily Plan', icon: CalendarDays },
    { to: '/meetings', label: 'Meetings', icon: ClipboardList },
    { to: '/users', label: 'Users', icon: Users },
    { to: '/templates', label: 'Templates', icon: FileText },
    { to: '/team-targets', label: 'Team Targets', icon: BarChart3 },
  ];

  const memberLinks = [
    { to: '/dashboard', label: 'My Dashboard', icon: LayoutDashboard },
    { to: '/goals', label: 'My Goals', icon: Target },
    { to: '/daily-plan', label: 'Daily Plan', icon: CalendarDays },
    { to: '/meetings', label: 'Meetings', icon: ClipboardList },
  ];

  // Choose links based on role
  let links;
  if (isSuperAdmin()) {
    links = superAdminLinks;
  } else if (isAdminOrFounder()) {
    links = adminFounderLinks;
  } else {
    links = memberLinks;
  }

  return (
    <aside className="sidebar flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-100">
        <Link to={isSuperAdmin() ? "/platform" : "/dashboard"} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1E2580' }}>
            {isSuperAdmin() ? (
              <Shield className="w-5 h-5 text-white" />
            ) : (
              <CheckSquare className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <h1 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: '#1E2580' }}>
              TeamSync
            </h1>
            <p className="text-xs" style={{ color: '#2B2B2B' }}>
              {isSuperAdmin() ? 'Platform Admin' : (user?.company_name || 'by DHIVOLVE')}
            </p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {links.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.to;
          return (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'sidebar-link',
                isActive && 'active'
              )}
            >
              <Icon className="w-5 h-5" />
              <span>{link.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-3 py-2 mb-3">
          <div className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center",
            isSuperAdmin() ? "bg-purple-100" : "bg-slate-200"
          )}>
            <span className={cn(
              "text-sm font-semibold",
              isSuperAdmin() ? "text-purple-600" : "text-slate-600"
            )}>
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className={cn(
              "text-xs capitalize",
              isSuperAdmin() ? "text-purple-600 font-medium" : "text-slate-500"
            )}>
              {user?.role === 'super_admin' ? 'Super Admin' : user?.role}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </Button>
      </div>
    </aside>
  );
};
